import { useState, useMemo, useEffect } from "react";

import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "V2.0/common/dialog";

import InputComponent from "V2.0/components/InputComponent";
import { useAssistants } from "V2.0/HomePages/Assistants";
import { useAssistantOptions } from "../../HomeCampaign/useAssistantOptions";
// Imported to match Frontend's integration (bulk-campaign-report-ui-port, Task 7, Step 4) — note
// Frontend's own BulkCampaignCreate.tsx imports this but never calls it either; kept as a faithful
// port rather than silently dropping what the plan explicitly called out to add. See task report.
import { useAssistantPhoneNumbers } from "../../HomeCampaign/useAssistantPhoneNumbers";
import BulkSmsCampaignForm from "./BulkCampaignForm/Sms";
import BulkCallCampaignForm from "./BulkCampaignForm/Call";
import BulkWhatsAppCampaignForm from "./BulkCampaignForm/WhatsApp";
import { message, Tooltip } from "antd";
// FIX4: used to show the selected assistant's office hours (today's working time) in the notice below.
import moment from "moment-timezone";
import { showErrorToast } from "V2.0/utils";
import { Campaign_Type_Label } from "V2.0/HomePages/HomeCampaign/SingleCampaignAdd";
import IgnorePreviousCampaigns, {
  useIgnorePreviousCampaigns,
} from "V2.0/HomePages/HomeCampaign/common/Fields/IgnorePreviousCampaigns";
import { RadioGroup, Radio, RadioField } from "V2.0/common/radio";
import { Label, Field, Description } from "V2.0/common/fieldset";
import useBulkCampaignFromIdentifier from "./BulkCampaignFromIdentifier";
import { CampaignTypeIcons } from "../common/CampaignTypeIcons";

export const CAMPAIGN_TYPE = {
  SMS: "sms",
  CALL: "call",
  WHATSAPP: "whatsapp",
  // No EMAIL entry — Catalyst has no Email bulk-campaign form and must never surface an Email
  // option anywhere in this create flow (bulk-campaign-report-ui-port Global Constraints).
};

const ASSISTANT_CAMPAIGN_MAPPING = {
  [CAMPAIGN_TYPE.SMS]: "sms",
  [CAMPAIGN_TYPE.CALL]: "voice",
  [CAMPAIGN_TYPE.WHATSAPP]: "whatsapp",
};

const CampaignAddRegistry = {
  [CAMPAIGN_TYPE.SMS]: (props) => <BulkSmsCampaignForm {...props} />,
  [CAMPAIGN_TYPE.CALL]: (props) => <BulkCallCampaignForm {...props} />,
  [CAMPAIGN_TYPE.WHATSAPP]: (props) => <BulkWhatsAppCampaignForm {...props} />,
  // NOTE: Frontend also has an EMAIL: (props) => <BulkEmailCampaignForm .../> entry here — deliberately
  // dropped. No such form exists or should exist in Catalyst (bulk-campaign-report-ui-port, Task 7).
};

// Pill-style campaign-type selector, ported from Frontend with the violet/email entry dropped
// (bulk-campaign-report-ui-port, Task 7 Step 2).
const CAMPAIGN_TYPE_TABS = [
  {
    key: CAMPAIGN_TYPE.CALL,
    badgeClassName: "bg-amber-50 text-amber-500",
    activeClassName:
      "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
  },
  {
    key: CAMPAIGN_TYPE.SMS,
    badgeClassName: "bg-blue-50 text-blue-500",
    activeClassName:
      "border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-200",
  },
  {
    key: CAMPAIGN_TYPE.WHATSAPP,
    badgeClassName: "bg-green-50 text-green-500",
    activeClassName:
      "border-green-300 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-950/40 dark:text-green-200",
  },
  // NOTE: Frontend also has a violet/email tab entry here — deliberately dropped, no Email channel
  // in Catalyst (bulk-campaign-report-ui-port, Task 7 Step 2).
];

function CampaignTypeTabs({ value, onChange }) {
  const hasSelection = Boolean(value);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {CAMPAIGN_TYPE_TABS.map((tab) => {
        const isActive = tab.key === value;
        const badge = (
          <span
            className={`flex size-6 shrink-0 items-center justify-center rounded-full ${tab.badgeClassName}`}
          >
            {CampaignTypeIcons[tab.key]}
          </span>
        );

        if (!hasSelection) {
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange(tab.key)}
              className="flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-2.5 text-sm font-medium text-zinc-500 transition-all hover:bg-zinc-50 hover:text-zinc-700"
            >
              {badge}
              {Campaign_Type_Label[tab.key]}
            </button>
          );
        }

        if (isActive) {
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange(tab.key)}
              className={`flex shrink-0 items-center gap-x-2 rounded-lg border px-4 py-2.5 text-sm font-medium shadow-sm transition-all ${tab.activeClassName}`}
            >
              {badge}
              {Campaign_Type_Label[tab.key]}
            </button>
          );
        }

        return (
          <Tooltip
            key={tab.key}
            title={Campaign_Type_Label[tab.key]}
          >
            <button
              type="button"
              onClick={() => onChange(tab.key)}
              className="flex shrink-0 items-center justify-center rounded-lg border border-transparent px-3 py-2.5 text-zinc-500 transition-all hover:bg-zinc-50 hover:text-zinc-700"
            >
              {badge}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

// FIX4: the campaign-level "Business Hours" tab was removed; campaign timing now follows the
// selected assistant's office hours. This notice appears once an assistant is picked and shows that
// rule plus the assistant's working hours for TODAY (computed in the assistant's own timezone).
// (bulk-campaign-report-ui-port, Task 7 Step 3) — Catalyst's create flow had no campaign-level
// "Business Hours" tab to remove, so this is a pure addition here, not a replacement.
const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// Slot times are stored as 24h "HH:mm"; render a friendly 12h label (e.g. "10:00 AM").
function formatSlotTime(t) {
  return moment(t, "HH:mm").format("h:mm A");
}

function AssistantHoursNotice({ assistant }) {
  const timezoneSettings = assistant?.assistantSettings?.timezoneSettings;
  const scheduleData = timezoneSettings?.scheduleData;

  // Nothing to show until an assistant with an office-hours schedule is selected.
  if (!assistant || !scheduleData) return null;

  const timezone = timezoneSettings?.timezone;
  const now = timezone ? moment.tz(timezone) : moment();
  const today = scheduleData[DAY_KEYS[now.day()]];

  let todayText;
  if (today?.checked && Array.isArray(today.slots) && today.slots.length > 0) {
    todayText = today.slots
      .map((s) => `${formatSlotTime(s.startTime)} – ${formatSlotTime(s.endTime)}`)
      .join(", ");
  } else {
    // Day is unchecked or has no slots → no calls today.
    todayText = "Closed";
  }

  return (
    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200">
      <p>
        Campaign timing follows this assistant's working hours
        {timezone ? ` (${timezone})` : ""}.
      </p>
      <p className="mt-1">
        Today ({now.format("dddd")}):{" "}
        <span className="font-semibold">{todayText}</span>
      </p>
      <p className="mt-1 text-xs opacity-80">
        You can change these hours in the assistant's Office Hours settings.
      </p>
    </div>
  );
}

// Old version: campaignType was chosen by the caller (BulkCampaigns/index.tsx's "Start Campaign"
// dropdown) and passed in as a prop before this dialog ever opened. Replaced below — campaignType is
// now local state driven by CampaignTypeTabs inside the dialog itself, matching Frontend's flow.
// Catalyst's Task 8 rewrite of index.tsx keeps its own dropdown as the entry point (per the plan's
// explicit deviation there, so Email stays excluded independent of which list-page tab is active),
// but still opens this same dialog, which now ignores any campaignType prop and lets the user
// pick/change the channel from the tabs below. (bulk-campaign-report-ui-port, Task 7)
// export function BulkCampaignCreate(props) {
//   const {
//     createBulkCampaign,
//     dialogState,
//     onClose,
//     workspaceId,
//     campaignType,
//   } = props;
//   const { assistants, fetchAssistants } = useAssistants(workspaceId);
//   const { assistantOptions, assistantId, setAssistantId } = useAssistantOptions(
//     assistants,
//     campaignType,
//   );
//   ...
// }

export function BulkCampaignCreate(props) {
  const {
    createBulkCampaign,
    dialogState,
    onClose,
    workspaceId,
    // Catalyst keeps its dropdown-based "Start Campaign" trigger (index.tsx, Task 8 deviation from
    // Frontend's single-button/tab-driven trigger, required so Email-exclusion in that dropdown stays
    // independent of the list page's active tab) — the dropdown already picks a channel before this
    // dialog opens, so that choice seeds the in-dialog CampaignTypeTabs selector's initial value
    // instead of always starting unset. The dialog only mounts while open (see index.tsx), so this
    // only needs to seed useState once per open, not stay in sync via an effect.
    campaignType: initialCampaignType,
  } = props;
  const [campaignType, setCampaignType] = useState<string | null>(
    initialCampaignType ?? null,
  );
  const { assistants, fetchAssistants } = useAssistants(workspaceId);
  const { assistantOptions, assistantId, setAssistantId } = useAssistantOptions(
    assistants,
    campaignType,
  );

  useEffect(() => {
    setAssistantId("");
  }, [campaignType]);
  const {
    fromIdentifier,
    availableIdentifiers,
    rotateIdentifiers,
    fromIdentifierComponentProps,
    identifierModeComponentProps,
    FromIdentifierComponent,
    IdentifierModeComponent,
  } = useBulkCampaignFromIdentifier(workspaceId, assistantId, campaignType);

  const { isIgnorePreviousCampaigns, toggleIgnorePreviousCampaigns } =
    useIgnorePreviousCampaigns();

  const [bulkCampaignName, setBulkCampaignName] = useState("");
  const [bulkCampaignDescription, setBulkCampaignDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<"Sheet" | "CSV">("Sheet");

  function onCreateBulkCampaign(payload) {
    const lifecycleCbs = {
      init: () => {
        message.loading("Creating Campaign");
      },
      success: () => {
        message.destroy();
        message.success("Campaign Created");
        setLoading(false);
        onClose();
      },
      error: (e) => {
        message.destroy();
        setLoading(false);
        showErrorToast(e, "Failed to create campaign");
      },
    };

    createBulkCampaign(payload, lifecycleCbs);
  }

  useEffect(() => {
    if (workspaceId) {
      fetchAssistants();
    }
  }, [workspaceId]);

  return (
    <Dialog
      open={dialogState.isOpen}
      onClose={onClose}
      size="2xl"
    >
      <DialogTitle>New bulk campaign</DialogTitle>
      <DialogDescription>
        Pick a channel, point at a list, see exactly what goes out.
      </DialogDescription>
      <DialogBody className="max-h-[75vh] overflow-y-auto">
        <CampaignTypeTabs
          value={campaignType}
          onChange={setCampaignType}
        />

        {campaignType ? (
          <div className="mt-4 flex min-w-0 flex-col gap-y-3">
            <InputComponent
              label="Campaign Name *"
              type="text"
              onChange={(e) => setBulkCampaignName(e.target.value)}
            />
            <InputComponent
              label="Campaign Description"
              type="textArea"
              style={{ fontSize: "13px", height: "80px", marginBottom: "8px" }}
              onChange={(e) => setBulkCampaignDescription(e.target.value)}
            />
            <InputComponent
              label="Assistant *"
              type="select"
              options={assistantOptions}
              value={assistantId}
              onChange={(e) => setAssistantId(e.target.value)}
            />
            {assistantId && (
              <AssistantHoursNotice
                assistant={assistants.find((a) => a._id === assistantId)}
              />
            )}
            {campaignType === CAMPAIGN_TYPE.CALL ? (
              <div className="flex flex-col gap-y-3">
                <IdentifierModeComponent {...identifierModeComponentProps} />
                {!rotateIdentifiers && (
                  <FromIdentifierComponent {...fromIdentifierComponentProps} />
                )}
              </div>
            ) : (
              <FromIdentifierComponent {...fromIdentifierComponentProps} />
            )}
            <IgnorePreviousCampaigns
              isIgnored={isIgnorePreviousCampaigns}
              onToggleIgnore={toggleIgnorePreviousCampaigns}
            />
            {campaignType === "call" && (
              <Field className="flex">
                <RadioGroup
                  className="!space-y-0 flex w-full gap-4"
                  value={source}
                >
                  <RadioField
                    className={`flex-1 rounded-lg border p-4 cursor-pointer transition-colors ${
                      source === "Sheet"
                        ? "border-zinc-950 bg-zinc-950/5 dark:border-white dark:bg-white/5"
                        : "border-zinc-200 dark:border-zinc-800"
                    }`}
                  >
                    <Radio
                      value="Sheet"
                      onClick={() => setSource("Sheet")}
                    />
                    <Label>Google Sheet URL</Label>
                    <Description>Import from Google Sheet</Description>
                  </RadioField>
                  <RadioField
                    className={`flex-1 rounded-lg border p-4 cursor-pointer transition-colors ${
                      source === "CSV"
                        ? "border-zinc-950 bg-zinc-950/5 dark:border-white dark:bg-white/5"
                        : "border-zinc-200 dark:border-zinc-800"
                    }`}
                  >
                    <Radio
                      value="CSV"
                      onClick={() => setSource("CSV")}
                    />
                    <Label>CSV File</Label>
                    <Description>Upload a CSV file</Description>
                  </RadioField>
                </RadioGroup>
              </Field>
            )}

            {CampaignAddRegistry[campaignType]({
              createBulkCampaign: onCreateBulkCampaign,
              onClose,
              workspaceId,
              assistantId,
              fromNumber: fromIdentifier,
              fromEmail: fromIdentifier,
              rotateNumbers:
                rotateIdentifiers && availableIdentifiers.length > 0,
              availableIdentifiers,
              name: bulkCampaignName,
              description: bulkCampaignDescription,
              loading,
              setLoading,
              ignorePreviousCampaigns: isIgnorePreviousCampaigns,
              source,
            })}
          </div>
        ) : (
          <p className="mt-6 text-center text-sm text-zinc-400">
            Pick a channel above to continue
          </p>
        )}
      </DialogBody>
    </Dialog>
  );
}
